import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { message } from 'antd';
import api, { userPreferencesApi } from '../services/api';
import { claimStoredReferral } from '../services/referral';
import { storedLandingRef, clearLandingRef } from '../services/kbLandingRef';
import { useLanguage } from './LanguageContext';
import { useTranslation } from 'react-i18next';
import { auth, googleProvider } from '../config/firebase';
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  getIdToken,
  onAuthStateChanged,
  EmailAuthProvider,
  linkWithCredential,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  updatePassword,
  sendPasswordResetEmail,
} from 'firebase/auth';

export interface User {
  id: string;
  email: string;
  name: string;
  created_at: string;
  updated_at: string;
  // Platform-admin flags from GET /api/auth/me. Gate the /admin nav only;
  // real enforcement is server-side (admin_middleware).
  is_admin?: boolean;
  admin_role?: string | null;
  // 'invited' = member account (created because a KB invitation was pending
  // for this email); no personal KB entitlement unless subscribed.
  account_origin?: 'self' | 'invited';
  // From GET /api/auth/me. False only for unsubscribed member accounts.
  // Gates the create-KB UI; the server gate (402 feature_locked) is the
  // real enforcement, so treat undefined as allowed.
  can_own_kbs?: boolean;
}

export interface UserAuthProvider {
  id: string;
  provider_type: string;
  provider_email: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, name: string) => Promise<boolean>;
  loginWithGoogle: () => Promise<boolean>;
  registerWithGoogle: (referralCode?: string) => Promise<boolean>;
  registerWithFirebase: (email: string, password: string, name: string, subscribeToOfficialKbs?: boolean, referralCode?: string) => Promise<boolean>;
  bindOAuthProvider: (idToken: string, providerType: string) => Promise<boolean>;
  unbindOAuthProvider: (providerType: string) => Promise<boolean>;
  getAuthProviders: () => Promise<UserAuthProvider[]>;
  setPassword: (newPassword: string) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  sendPasswordReset: (email: string) => Promise<boolean>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { setLanguage, currentLanguage } = useLanguage();
  // Every toast below is user-facing: it must speak the language the user
  // picked, not the language this file happened to be written in.
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const tokenValidatedRef = useRef(false); // Track if token has been validated
  const langAppliedRef = useRef(false); // Apply persisted language once per session

  // On entering the authenticated app, set the UI language from the user's
  // persisted preference (users.language). Authoritative over the ephemeral
  // nav/homepage switcher; runs once when the user first loads. A null
  // preference is a no-op (browser/localStorage default stays).
  useEffect(() => {
    if (!user || langAppliedRef.current) return;
    langAppliedRef.current = true;
    userPreferencesApi
      .get()
      .then(prefs => {
        if (prefs.language) setLanguage(prefs.language);
      })
      .catch(() => {
        /* non-fatal: keep current UI language */
      });
  }, [user, setLanguage]);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let isMounted = true;

    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      console.log('AuthContext: Initializing, token exists:', !!savedToken);
      
      if (savedToken) {
        // Validate token with backend immediately, regardless of Firebase state
        console.log('AuthContext: Validating token with backend...');
        try {
          const response = await api.get('/api/auth/me');
          // Token is valid! Set user state immediately
          if (isMounted) {
            setUser({ ...response.data.user, is_admin: response.data.is_admin, admin_role: response.data.admin_role, can_own_kbs: response.data.can_own_kbs });
            setToken(savedToken);
            tokenValidatedRef.current = true;
            setLoading(false);
            console.log('AuthContext: Token validated, user set:', response.data.user.email);
          }
        } catch (error) {
          console.error('AuthContext: Token validation failed:', error);
          if (isMounted) {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            tokenValidatedRef.current = false;
            setLoading(false);
          }
          // Don't return - still set up Firebase listener in case Firebase can restore
        }
      } else {
        // No token, set loading to false
        if (isMounted) {
          setLoading(false);
        }
      }

      // Set up Firebase auth state listener
      // This handles Firebase user restoration and token updates
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        console.log('AuthContext: Firebase auth state changed, user:', firebaseUser ? 'exists' : 'null', 'tokenValidated:', tokenValidatedRef.current);
        
        if (!isMounted) return;
        
        if (firebaseUser) {
          // Firebase user is available
          try {
            const idToken = await getIdToken(firebaseUser);
            localStorage.setItem('token', idToken);
            setToken(idToken);
            
            // Verify with backend and update user state
            const response = await api.get('/api/auth/me');
            if (isMounted) {
              setUser({ ...response.data.user, is_admin: response.data.is_admin, admin_role: response.data.admin_role, can_own_kbs: response.data.can_own_kbs });
              tokenValidatedRef.current = true;
              setLoading(false);
            }
          } catch (error) {
            console.error('AuthContext: Failed to get Firebase token:', error);
            // Only clear state if token wasn't already validated
            if (!tokenValidatedRef.current && isMounted) {
              localStorage.removeItem('token');
              setToken(null);
              setUser(null);
              setLoading(false);
            }
          }
        } else {
          // No Firebase user
          // If token was already validated, don't clear state
          if (tokenValidatedRef.current) {
            console.log('AuthContext: Token already validated, Firebase user null is OK');
            return; // Don't clear state
          }
          
          // Token not validated yet, check if we have a token
          const savedToken = localStorage.getItem('token');
          if (savedToken) {
            // Try to validate token one more time
            try {
              const response = await api.get('/api/auth/me');
              if (isMounted) {
                setUser({ ...response.data.user, is_admin: response.data.is_admin, admin_role: response.data.admin_role, can_own_kbs: response.data.can_own_kbs });
                setToken(savedToken);
                tokenValidatedRef.current = true;
                setLoading(false);
              }
            } catch (error) {
              // Token validation failed, clear it
              console.error('AuthContext: Token validation failed:', error);
              if (isMounted) {
                localStorage.removeItem('token');
                setToken(null);
                setUser(null);
                tokenValidatedRef.current = false;
                setLoading(false);
              }
            }
          } else {
            // No token
            if (isMounted) {
              setToken(null);
              setUser(null);
              setLoading(false);
            }
          }
        }
      });
    };

    initAuth();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // /api/auth/firebase returns only { token, user } — the admin flags live on
  // /api/auth/me. Without this, user.is_admin stays undefined after a fresh
  // login and the sidebar hides the Admin Console entry until a page reload.
  const hydrateAdminFlags = async (fallback: User) => {
    try {
      const me = await api.get('/api/auth/me');
      setUser({ ...me.data.user, is_admin: me.data.is_admin, admin_role: me.data.admin_role, can_own_kbs: me.data.can_own_kbs });
    } catch {
      setUser(fallback);
    }
  };

  // KB share-page attribution (/kb/:slug → register). Sent on every
  // /api/auth/firebase call; the backend only records it on first signup
  // (was_created), so including it on plain logins is harmless.
  const landingRefFields = () => {
    const ref = storedLandingRef();
    return ref ? { kb_landing_slug: ref.slug, kb_landing_visitor_id: ref.visitor_id } : {};
  };

  // Login with Firebase Authentication (Email/Password)
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      console.log('AuthContext: Attempting Firebase login for:', email);
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Get Firebase ID token
      const idToken = await getIdToken(firebaseUser);
      
      // Send to backend to get/create user record
      const response = await api.post('/api/auth/firebase', { id_token: idToken, language: currentLanguage, ...landingRefFields() });
      const { token: newToken, user: userData } = response.data;

      console.log('AuthContext: Login successful, setting token and user');
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      tokenValidatedRef.current = true;
      await hydrateAdminFlags(userData);

      await claimStoredReferral();
      clearLandingRef();
      message.success(t('auth.loginSuccess'));
      return true;
    } catch (error: any) {
      console.error('AuthContext: Login failed:', error);
      const errorMessage = error.code === 'auth/user-not-found'
        ? t('auth.userNotFound', 'User not found')
        : error.code === 'auth/wrong-password'
        ? t('auth.wrongPassword', 'Incorrect password')
        : error.code === 'auth/invalid-email'
        ? t('auth.emailInvalid')
        : t('auth.loginFailed');
      message.error(errorMessage);
      return false;
    }
  };

  // Register is deprecated - use registerWithFirebase instead
  const register = async (email: string, password: string, name: string): Promise<boolean> => {
    // Redirect to registerWithFirebase
    return registerWithFirebase(email, password, name);
  };

  // Register with Firebase Authentication (Email/Password).
  // `subscribeToOfficialKbs` is the Register page checkbox value — when true,
  // the backend auto-inserts subscriptions to every is_pitto_official KB.
  // Only honoured on first-time signup (backend gates on was_created).
  const registerWithFirebase = async (email: string, password: string, name: string, subscribeToOfficialKbs: boolean = false, referralCode?: string): Promise<boolean> => {
    try {
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Update Firebase user profile with displayName (this will be available in token claims)
      // Note: The backend will extract the name from Firebase token claims
      const { updateProfile } = await import('firebase/auth');
      await updateProfile(firebaseUser, { displayName: name });

      // Get fresh token with updated profile
      const idToken = await getIdToken(firebaseUser, true); // Force refresh to get updated claims

      // Send to backend to create/update user record
      const response = await api.post('/api/auth/firebase', {
        id_token: idToken,
        subscribe_to_official_kbs: subscribeToOfficialKbs,
        // Captured on first signup → persisted preference + welcome-email language.
        language: currentLanguage,
        // Referral link code (?ref=) → pending referral on first signup.
        referral_code: referralCode,
        // KB share-page attribution → `registered` funnel event on first signup.
        ...landingRefFields(),
      });
      const { token: newToken, user: userData } = response.data;
      
      // Note: newToken is the Firebase ID token, which will be managed by Firebase SDK
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      tokenValidatedRef.current = true;
      await hydrateAdminFlags(userData);

      await claimStoredReferral();
      clearLandingRef();
      message.success(t('auth.registerSuccess'));
      return true;
    } catch (error: any) {
      console.error('Firebase registration failed:', error);
      const errorMessage = error.code === 'auth/email-already-in-use'
        ? t('auth.emailAlreadyRegistered', 'Email already registered')
        : t('auth.registerFailed');
      message.error(errorMessage);
      return false;
    }
  };

  // Login with Google OAuth
  const loginWithGoogle = async (referralCode?: string): Promise<boolean> => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      const idToken = await getIdToken(firebaseUser);

      // Send to backend. `referral_code` only matters on first-time signup
      // (backend gates on was_created); a plain login ignores it.
      const response = await api.post('/api/auth/firebase', { id_token: idToken, language: currentLanguage, referral_code: referralCode, ...landingRefFields() });
      const { token: newToken, user: userData } = response.data;
      
      // Note: newToken is the Firebase ID token, which will be managed by Firebase SDK
      // onAuthStateChanged will update the token automatically
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      tokenValidatedRef.current = true;
      await hydrateAdminFlags(userData);

      await claimStoredReferral();
      clearLandingRef();
      message.success(t('auth.loginSuccess'));
      return true;
    } catch (error: any) {
      console.error('Google login failed:', error);
      if (error.code !== 'auth/popup-closed-by-user') {
        message.error(t('auth.googleLoginFailed', 'Google login failed'));
      }
      return false;
    }
  };

  // Register with Google OAuth (same as login, creates account if doesn't exist)
  const registerWithGoogle = async (referralCode?: string): Promise<boolean> => {
    return loginWithGoogle(referralCode); // Firebase handles account creation automatically
  };

  // Bind OAuth provider to existing account
  const bindOAuthProvider = async (idToken: string, providerType: string): Promise<boolean> => {
    try {
      await api.post('/api/auth/bind-oauth', { 
        id_token: idToken, 
        provider_type: providerType 
      });
      message.success(t('auth.oauthBindSuccess', 'OAuth provider bound successfully'));
      return true;
    } catch (error: any) {
      console.error('Bind OAuth provider failed:', error);
      message.error(t('auth.oauthBindFailed', 'Failed to bind OAuth provider'));
      return false;
    }
  };

  // Unbind OAuth provider
  const unbindOAuthProvider = async (providerType: string): Promise<boolean> => {
    try {
      await api.post('/api/auth/unbind-oauth', { provider_type: providerType });
      message.success(t('auth.oauthUnbindSuccess', 'OAuth provider unbound successfully'));
      return true;
    } catch (error: any) {
      console.error('Unbind OAuth provider failed:', error);
      message.error(t('auth.oauthUnbindFailed', 'Failed to unbind OAuth provider'));
      return false;
    }
  };

  // Get authentication providers for current user
  const getAuthProviders = async (): Promise<UserAuthProvider[]> => {
    try {
      const response = await api.get('/api/auth/providers');
      return response.data.providers || [];
    } catch (error: any) {
      console.error('Get auth providers failed:', error);
      return [];
    }
  };

  // Set a password on an account that has none yet (e.g. Google-only signup).
  // The password lives entirely in Firebase — we link an Email/Password
  // credential to the SAME Firebase user, then mirror the `email` provider into
  // our DB via /api/auth/bind-oauth so it shows up in the providers list.
  const setPassword = async (newPassword: string): Promise<boolean> => {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      message.error(t('auth.reauthRequired', 'Please sign in again to manage your password'));
      return false;
    }
    try {
      const cred = EmailAuthProvider.credential(currentUser.email, newPassword);
      try {
        await linkWithCredential(currentUser, cred);
      } catch (error: any) {
        // Firebase requires a recent login for credential changes; re-auth via
        // the existing Google session and retry once.
        if (error.code === 'auth/requires-recent-login') {
          await reauthenticateWithPopup(currentUser, googleProvider);
          await linkWithCredential(currentUser, cred);
        } else {
          throw error;
        }
      }
      const idToken = await getIdToken(currentUser, true);
      await api.post('/api/auth/bind-oauth', { id_token: idToken, provider_type: 'email' });
      message.success(t('auth.passwordSetSuccess', 'Password set successfully'));
      return true;
    } catch (error: any) {
      const msg = error.code === 'auth/provider-already-linked'
        ? t('auth.passwordAlreadySet', 'A password is already set for this account')
        : error.code === 'auth/email-already-in-use'
        ? t('auth.emailAlreadyHasPassword', 'This email already has a password login')
        : error.code === 'auth/weak-password'
        ? t('auth.passwordMinLength')
        : t('auth.setPasswordFailed', 'Failed to set password');
      message.error(msg);
      return false;
    }
  };

  // Change the password on an account that already has an email/password login.
  // Re-authenticate with the current password, then update — no backend call,
  // the email provider is already recorded.
  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      message.error(t('auth.reauthRequired', 'Please sign in again to manage your password'));
      return false;
    }
    try {
      const cred = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, cred);
      await updatePassword(currentUser, newPassword);
      message.success(t('auth.passwordUpdateSuccess', 'Password updated successfully'));
      return true;
    } catch (error: any) {
      const msg = (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential')
        ? t('auth.currentPasswordIncorrect', 'Current password is incorrect')
        : error.code === 'auth/weak-password'
        ? t('auth.passwordMinLength')
        : t('auth.updatePasswordFailed', 'Failed to update password');
      message.error(msg);
      return false;
    }
  };

  // Send a Firebase password-reset email. Resolve to a generic success even on
  // user-not-found so we don't leak which emails have accounts.
  const sendPasswordReset = async (email: string): Promise<boolean> => {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error: any) {
      if (error.code === 'auth/invalid-email') {
        message.error(t('auth.emailInvalid'));
        return false;
      }
      console.error('Password reset failed:', error);
      return true;
    }
  };

  const logout = async () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    tokenValidatedRef.current = false;
    // Sign out from Firebase as well
    if (auth.currentUser) {
      await auth.signOut().catch(console.error);
    }
    message.success(t('auth.logoutSuccess', 'Logged out successfully'));
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    loginWithGoogle,
    registerWithGoogle,
    registerWithFirebase,
    bindOAuthProvider,
    unbindOAuthProvider,
    getAuthProviders,
    setPassword,
    changePassword,
    sendPasswordReset,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
